import { useEffect, useState } from 'react'
import { Col, Form, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import ReactSelect from 'react-select'
import CreatableSelect from 'react-select/creatable'

import { postOrganizationDocument } from '../../actions/organization/organizationdocumentpost'
import { tagManagerPersist } from '../../api'
import { AttachmentView } from '../../common/attachments/attachmentview'
import { Button } from '../../common/Button.tsx'
import DefaultDatePicker from '../../common/datepicker/DefaultDatePicker.jsx'
import { IconAddNew } from '../../common/icons'
import { creatableSelectTagSettings, zIndexFix } from '../../common/reactselect'
import Switch from '../../common/Switch.jsx'
import { fetchTags } from '../../common/utils'
import { actionTypeSelector, authSelector } from '../../reducers'

export function AddDocument({ show, setShow, documentData, organizationId, businessIdentifier }) {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const actionType = useSelector(actionTypeSelector)
	const auth = useSelector(authSelector)
	const isBasicLevel = auth.hrLevel !== 'premium'
	const supervisor = auth.role === 'SUPERVISOR'
	const prefix = 'documentModal'

	const documentLocationOptions = {
		DOCUMENTS: t('Documents', 'Dokumentit'),
		ONBOARDING: 'Onboarding',
		OFFBOARDING: 'Offboarding',
		USERPORTAL: t('UserPortal', 'User portal'),
		OWNPROFILE: t('OwnProfile', 'Oma profiili'),
	}

	const documentViewerOptions = [
		{
			value: 'HR',
			label: t('HR', 'HR'),
		},
		{
			value: 'SUPERVISOR',
			label: t('Supervisor', 'Esihenkilö'),
		},
		{
			value: 'EMPLOYEE',
			label: t('Employee', 'Työntekijä'),
		},
	]

	const [data, setData] = useState(null)

	const [attachmentList, setAttachmentList] = useState([])

	const listChanged = (list) => {
		setAttachmentList(list)
	}

	const onSubmit = (data) => {
		if (!data.documentLocation) data.documentLocation = null

		const attachmentType = attachmentList?.[0]?.type

		const payload = {
			...data,
			documentCategory: data.documentCategory ?? attachmentType ?? null,
		}

		dispatch(postOrganizationDocument(organizationId, data?.documentId ?? -1, { ...payload, businessIdentifier }))
	}

	const { register, reset, handleSubmit, setValue, watch, control } = useForm({
		defaultValues: data,
	})

	useEffect(() => {
		setData(documentData)
		reset(documentData)
	}, [documentData, reset])

	const [tagOptions, setTagOptions] = useState([])

	useEffect(() => {
		fetchTags(setTagOptions)
	}, [])

	const documentLocation = watch('documentLocation')
	const documentViewers = watch('documentViewers') || []

	useEffect(() => {
		if (documentLocation === 'OWNPROFILE') {
			setValue('documentViewers', ['EMPLOYEE'])
		}
	}, [documentLocation, setValue])

	const documentViewersValue = documentViewers.map((viewer) =>
		documentViewerOptions.find((option) => option.value === viewer),
	)

	const handleTags = (options) =>
		setValue(
			'tags',
			options.map((option) => ({ codeItemId: option.value, codeKey: option.label })),
		)

	const handleCreateTag = async (value) => {
		const res = await tagManagerPersist([{ codeKey: value }])
		const { codeItemId, codeKey } = res.data[0]
		setTagOptions([...tagOptions, { value: codeItemId, label: codeKey }])
		const currentTags = data.tags ?? []
		setValue('tags', [...currentTags, { codeItemId: codeItemId, codeKey: codeKey }])
	}

	const handleClose = () => setShow(false)
	const handleShow = () => {
		const emptyData = { documentId: -1 }
		setData(emptyData)
		reset(emptyData)
		setShow(true)
	}

	const renderSwitch = (name, props) => (
		<Controller
			name={name}
			control={control}
			render={({ field }) => (
				<Switch {...props} checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
			)}
		/>
	)

	const isOwnProfile = documentLocation === 'OWNPROFILE'
	const tooManyAttachments = isOwnProfile && attachmentList.length > 1

	return (
		<>
			{!supervisor && (
				<Button className="addbutton" onClick={handleShow}>
					<IconAddNew /> {t('AddDocument', 'Lisää dokumentti')}
				</Button>
			)}

			{show && (
				<Modal show onHide={handleClose} backdrop="static" size="xl">
					<Modal.Header closeButton>
						<Modal.Title>{t('AddDocument', 'Lisää dokumentti')}</Modal.Title>
					</Modal.Header>
					<Form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Body className="ag-theme-material styles-ag p-4 content-div">
							<AttachmentView
								section="document"
								entityid={data?.documentId}
								attachments={data?.attachments}
								onListChange={listChanged}
								savedId={
									(actionType === 'POST_ORGANIZATION_DOCUMENT_SUCCEEDED' ||
										actionType === 'POST_ORGANIZATION_DOCUMENT_EDIT_SUCCEEDED') &&
									data?.documentId !== -1 &&
									data.documentId
								}
								readOnly={supervisor}
							/>

							{!isBasicLevel && (
								<>
									<h2>{t('DocumentSettings', 'Dokumentin asetukset')}</h2>
									<Row className="mb-1">
										<Form.Group as={Col} controlId="documentLocation">
											<Form.Label>{t('DocumentLocation', 'Missä näytetään?')}</Form.Label>
											<Form.Select disabled={supervisor} {...register('documentLocation')}>
												<option value="">{t('Select', 'Valitse')}</option>
												{Object.keys(documentLocationOptions).map((location) => (
													<option key={location} value={location}>
														{documentLocationOptions[location]}
													</option>
												))}
											</Form.Select>
										</Form.Group>
										<Form.Group as={Col} controlId="documentViewers">
											<Form.Label>{t('DocumentViewer', 'Kenelle näytetään?')}</Form.Label>
											<ReactSelect
												styles={zIndexFix}
												name="documentViewers"
												menuPlacement="bottom"
												menuPosition="absolute"
												options={documentViewerOptions}
												placeholder={t('Select', 'Valitse')}
												noOptionsMessage={() => t('NoOptions', 'Ei valintoja')}
												onChange={(values) => {
													if (documentLocation === 'OWNPROFILE') {
														setValue('documentViewers', ['EMPLOYEE'])
													} else {
														setValue(
															'documentViewers',
															values.map((item) => item.value),
														)
													}
												}}
												value={documentViewersValue}
												isMulti
												isDisabled={supervisor}
											/>
										</Form.Group>
									</Row>
									<Row className="mb-1">
										<Form.Group as={Col} controlId="dueDate">
											<Form.Label></Form.Label>
											{renderSwitch('dueDate', {
												id: 'dueDate',
												label: t('DocumentHasDueDate', 'Dokumentilla on eräpäivä'),
												className: 'mb-1',
												disabled: supervisor,
											})}
										</Form.Group>
										{watch('dueDate') && (
											<Form.Group as={Col} className="mt-3" controlId="documentValidUntil">
												<DefaultDatePicker
													id={`${prefix}-documentValidUntil`}
													name="documentValidUntil"
													label={t('CourseValidThru', 'Voimassaolon päät.pvm')}
													control={control}
													disabled={supervisor}
												/>
											</Form.Group>
										)}
									</Row>
									<Row className="mb-1">
										<Form.Group as={Col} controlId="documentReminder">
											<Form.Label></Form.Label>
											{renderSwitch('documentReminder', {
												id: 'documentReminder',
												label: t('ShowOnDashboard', 'Nostetaan etusivulle'),
												className: 'mb-1',
												disabled: supervisor,
											})}
										</Form.Group>
										{watch('documentReminder') && (
											<Form.Group as={Col} controlId="documentReminderDays">
												<Form.Label>{t('NotifyBeforeEnd', 'Ennen voimassaolon umpeutumista')}</Form.Label>
												<Form.Control
													type="number"
													placeholder={t('Days2', 'Päiviä')}
													{...register('documentReminderDays')}
													disabled={supervisor}
												/>
											</Form.Group>
										)}
									</Row>
									<Row className="mb-1">
										<Col>
											<Form.Group as={Col} controlId="documentkeywords">
												<Form.Label>{t('Keywords', 'Avainsanat')}</Form.Label>
												<CreatableSelect
													styles={zIndexFix}
													defaultValue={data?.tags?.map((tag) => ({ value: tag.codeItemId, label: tag.codeKey }))}
													onChange={handleTags}
													onCreateOption={handleCreateTag}
													options={tagOptions}
													isDisabled={supervisor}
													{...creatableSelectTagSettings(t)}
												/>
											</Form.Group>
										</Col>
										<Col />
									</Row>
								</>
							)}
						</Modal.Body>

						{!supervisor && (
							<Modal.Footer>
								<OverlayTrigger
									placement="top"
									overlay={
										tooManyAttachments ? (
											<Tooltip>
												{t(
													'OwnProfileAttachmentLimit',
													'Oman profiilin dokumentille on sallittu vain yksi liitetiedosto',
												)}
											</Tooltip>
										) : (
											<></>
										)
									}
								>
									<span>
										<Button variant="primary" type="submit" disabled={tooManyAttachments}>
											{t('Save', 'Tallenna')}
										</Button>
									</span>
								</OverlayTrigger>
							</Modal.Footer>
						)}
					</Form>
				</Modal>
			)}
		</>
	)
}
